import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTruckCatalog } from '../api/endpoints/truckTypes';
import { referenceQueryPolicy } from '../api/queryPolicies';
import { TRUCK_CATALOG } from '../config/truckCatalog';

/** One picker option; capacity/length/ideal-for are null when the admin left them blank. */
export interface CatalogTruck {
  id: string;
  name: string;
  capacityTons: string | null;
  lengthFt: string | null;
  idealFor: string | null;
}

export interface CatalogCategory {
  id: string;
  name: string;
  description: string;
  trucks: CatalogTruck[];
}

export const TRUCK_CATALOG_QUERY_KEY = ['truck-types'] as const;

export interface TruckCatalog {
  /** Non-empty categories, in API order. */
  categories: CatalogCategory[];
  /** Every active truck, flattened (lane pickers). */
  trucks: CatalogTruck[];
  /** 'api' once GET /truck-types answered; 'fallback' = the bundled copy (first load, offline, error). */
  source: 'api' | 'fallback';
  find: (id: string) => { category: CatalogCategory; truck: CatalogTruck } | undefined;
}

function build(categories: CatalogCategory[], source: TruckCatalog['source']): TruckCatalog {
  const shown = categories.filter((c) => c.trucks.length > 0);
  return {
    categories: shown,
    trucks: shown.flatMap((c) => c.trucks),
    source,
    find: (id) => {
      for (const category of shown) {
        const truck = category.trucks.find((t) => t.id === id);
        if (truck) return { category, truck };
      }
      return undefined;
    },
  };
}

const FALLBACK = build(TRUCK_CATALOG, 'fallback');

/**
 * The active truck catalog for every picker (#249 Contract section 5): GET /truck-types, with the
 * bundled `truckCatalog.ts` copy only while the first load is in flight or when the call fails.
 */
export function useTruckCatalog(): TruckCatalog {
  const { data } = useQuery({
    queryKey: TRUCK_CATALOG_QUERY_KEY,
    queryFn: ({ signal }) => getTruckCatalog(signal),
    ...referenceQueryPolicy,
  });
  return useMemo(
    () =>
      data
        ? build(
            data.categories.map((c) => ({ id: c.categoryId, name: c.categoryName, description: c.description, trucks: c.trucks })),
            'api',
          )
        : FALLBACK,
    [data],
  );
}
